import * as yup from 'yup';
import moment from 'moment';
import React, { useContext, useEffect, useState } from 'react';
import MetaTags from 'react-meta-tags';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import { Form, FormikHelpers, FormikValues } from 'formik';
import { ArnContext } from 'contexts';
import { IAuBreadcrumbItem, AuBreadcrumbs } from 'components/DTA';
import { ArnApplicationFormData } from 'types/forms/arnApplicationTypes';
import { FormContext, FormContextProvider, IFormContextType } from 'contexts/FormContext';
import { FormStep, IFormAlertType } from 'types/forms/formTypes';
import { FormAlert } from 'components/CASAForm';
import { AuFormItemWrapper } from 'components/DTA';
import PersonalDetailsMyGov from './PersonalDetailsMyGov';
import {
  AddAddressAndContact,
  DeclarationAndSubmit,
  FinalArnStep
} from '../Common';
import {
  addressesAndContactDetailsValidationSchema,
  personalDetailsMyGovIdPathValidationSchema
} from '../../validationSchemas/ValidationSchemas';
import { useArnService } from 'services';

import { FORM_ERROR_MESSAGES } from 'internalConstants';
import { urlSelector } from 'PortalStore/Selectors/EnvDataSelectors';
import { getPortalUrl } from 'components/Portal/Utilities';
import { Loader } from 'components/common';
import { ExitModal } from 'components/CASAForm/ExitModal';

type ArnParams = {
  arnType: string;
};

const ArnApplication: React.FC = () => {
  const loginPath = useSelector((state: any) => state.B2CSettings.items.loginPath);
  const myGovUser = useSelector((state: any) => state.B2CSettings.items.myGovUser);
  const { arnType } = useParams<ArnParams>();
  const { setArnType, hasArnOrPendingSubmissionResponse } = useContext(ArnContext);
  const B2CSettings = useSelector((state: any) => state.B2CSettings);
  const urlHome = useSelector((state: any) => urlSelector(state, 'Portal - Home URL'));
  const arnService = useArnService();
  const [isBackButtonClicked, setBackbuttonPress] = useState(false);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    window.history.pushState(window.location.search, '', window.location.pathname);
    window.addEventListener('popstate', onBackButtonEvent);
  }, []);

  const onBackButtonEvent = (e) => {
    e.preventDefault();
    if (!isBackButtonClicked) {
      setShowModal(true);
    }
  };

  const toggle = () => {
    setShowModal(!showModal);
    window.history.pushState(window.location.search, '', window.location.pathname);
    setBackbuttonPress(false);
  };

  const OK = () => {
    setShowModal(!showModal);
    setBackbuttonPress(true);
    window.history.back();
  };

  const exitModalParameters = {
    isOpen: showModal,
    toggleFunction: toggle,
    OKFunction: OK
  };

  if (loginPath != 'myGov') {
    if (loginPath == 'myCasa') {
      return arnType == 'new' ? <Redirect to='/arn-dvs/new' /> : <Redirect to='/arn-dvs/link' />;
    }
    return <Redirect to='/error' />;
  }

  useEffect(() => {
    setArnType(arnType);
  }, [arnType]);

  useEffect(() => {
    if (hasArnOrPendingSubmissionResponse?.wasSuccessful && hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission) {
      const dashboardUrl = getPortalUrl('/dashind', B2CSettings.items.standalone, urlHome);
      window.location.href = dashboardUrl;
    }
  }, [hasArnOrPendingSubmissionResponse]);

  const breadcrumbs: IAuBreadcrumbItem[] = [
    { link: '/', text: 'myCASA' },
    { text: arnType == 'new' ? 'Aviation reference number application' : 'Link aviation reference number application' }
  ];

  const myGovUserDoB = moment(myGovUser.dateOfBirth, 'yyyy-MM-DD', true);
  const initialValues: ArnApplicationFormData = {
    title: '',
    firstName: myGovUser.given_name,
    otherGivenNames: '',
    lastName: myGovUser.family_name,
    dateOfBirth: myGovUserDoB.format('YYYY-MM-DD'),
    sex: '',
    nationality: 'Australian',
    email: myGovUser.emails[0],
    address: {
      country: 'Australia',
      addressLookup: '',
      unitNumber: '',
      poBox: '',
      houseOrBuildingNumber: '',
      streetName: '',
      buildingName: '',
      cityOrSuburb: '',
      state: '',
      postcode: '',
      quickLookup: true
    },
    postalAddress: {
      country: 'Australia',
      addressLookup: '',
      poBox: '',
      unitNumber: '',
      houseOrBuildingNumber: '',
      streetName: '',
      buildingName: '',
      cityOrSuburb: '',
      state: '',
      postcode: '',
      quickLookup: true
    },
    isPostalAddressDifferent: '',
    mobileNumber: '',
    declaration: false
  };

  const errorAlert: IFormAlertType = {
    as: 'error',
    title: 'There was an issue submitting your application',
    message: 'Please try again'
  };

  const handleSubmit = async (values: ArnApplicationFormData, actions: FormikHelpers<ArnApplicationFormData & FormikValues>): Promise<IFormAlertType | null> => {
    const { setSubmitting } = actions;

    const arnApplicationPayLoad = {
      version: '01',
      name: 'ARN Application',
      rootFormCode: '',
      formCode: '',
      formData: values
    };

    try {
      // Send request to Submission service
      const response = arnType === 'new' ? await arnService.submitCreateArnApplication(arnApplicationPayLoad)
        : await arnService.submitLinkArnApplication(arnApplicationPayLoad);

      setSubmitting(false);
      // If request failed return error to the user
      if (!response || !response.wasSuccessful) {
        return errorAlert;
      }

      // If successful return success to the user
      return {
        as: 'success',
        title: arnType === 'new' ? 'myCASA account created and aviation reference number application successful'
          : 'myCASA account created and aviation reference number linked successfully'
      };
    }
    catch (error) {
      return errorAlert;
    }
  };

  const successfulSubmissionAlertTitle = `myCASA account created and aviation reference number ${arnType === 'new' ? 'application' : 'linked'} successful`;

  const STEPS: FormStep[] = [
    {
      id: 'PersonalDetails',
      render: (props) => (
        <PersonalDetailsMyGov nextStep='AddressAndContact' {...props} />
      ),
      validationSchema: personalDetailsMyGovIdPathValidationSchema
    },
    {
      id: 'AddressAndContact',
      render: (props) => (
        <AddAddressAndContact nextStep='DeclarationAndSubmit' {...props} />
      ),
      validationSchema: addressesAndContactDetailsValidationSchema
    },
    {
      id: 'DeclarationAndSubmit',
      render: () => (
        <DeclarationAndSubmit arnPath='mygov' arnType={arnType} alertTitle={successfulSubmissionAlertTitle} />
      ),
      validationSchema: yup.object().shape({
        declaration: yup.boolean()
          .oneOf([true], FORM_ERROR_MESSAGES.DECLARATION)
          .required(FORM_ERROR_MESSAGES.DECLARATION)
      })
    },
    {
      id: 'Final',
      render: () => (
        <FinalArnStep arnPath='mygov' arnType={arnType} successful={true} />
      ),
      skipNavigation: true
    }
  ];

  if (!hasArnOrPendingSubmissionResponse
    || hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission === undefined
    || hasArnOrPendingSubmissionResponse.hasArnOrPendingSubmission === true
    || hasArnOrPendingSubmissionResponse.wasSuccessful === false) {
    return <Loader message='Working. Please wait.....' />;
  }

  return (
    <>
      <MetaTags>
        <title>{arnType == 'new' ? 'Create ARN' : 'Link ARN'}</title>
      </MetaTags>
      <AuBreadcrumbs label='breadcrumbs' items={breadcrumbs} />
      <FormContextProvider
        initialValues={initialValues}
        handleSubmit={handleSubmit}
        steps={STEPS}>
        <FormContext.Consumer>
          {({ currentStepIndex, steps, currentStepProps }: IFormContextType) => (
            <Form name='ARN Application'>
              <AuFormItemWrapper>
                <FormAlert htmlFor='ARN Application' />
              </AuFormItemWrapper>
              <ExitModal exitModalParams={exitModalParameters} />
              {steps[currentStepIndex].render(currentStepProps)}
            </Form>
          )}
        </FormContext.Consumer>
      </FormContextProvider>
    </>
  );
};

export default ArnApplication;